<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";

import { type DataFrameWidgetProps } from "@/components/datascience/DataFrameWidget.vue";
import DataSourceToggle from "@/components/datascience/DataSourceToggle.vue";
import PlotlyWidget from "@/components/datascience/PlotlyWidget.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import { MediaType, type EstimatorReportDto } from "@/dto";
import { useClipboard } from "@/services/clipboard";
import AddToPresentationMenu from "@/views/AddToPresentationMenu.vue";

interface Dataframe extends DataFrameWidgetProps {
  value: string;
  method: string;
}

const { report } = defineProps<{ report: EstimatorReportDto }>();

const clipboard = useClipboard();

const selected = ref(0);
const isTestDatasource = ref(true);
const selectedPlot = useTemplateRef<HTMLElement>("selectedPlot");

const dataframes = computed<Dataframe[]>(() =>
  report.related_items
    .filter(
      (r) =>
        r.category === "feature_importance" &&
        r.representation.media_type === "application/vnd.dataframe"
    )
    .filter((r) => {
      if (r.attributes.data_source) {
        const source = isTestDatasource.value ? "test" : "train";
        return r.attributes.data_source === source;
      }
      return true;
    })
    .map(
      (r) =>
        ({
          method: r.attributes.method,
          indexNames: r.representation.value.index_names,
          ...r.representation.value,
        }) as unknown as Dataframe
    )
);
const boxplot = computed(() => {
  const df = dataframes.value[selected.value];
  const traces = [];
  for (const [i, row] of df.data.entries()) {
    traces.push({
      x: row,
      type: "box",
      name: df.index[i][1],
      boxpoints: false,
    });
  }
  const featureNameMaxLength = Math.max(...traces.map((v) => v.name.length));
  return {
    data: traces,
    layout: {
      shapes: traces.map((_, i) => ({
        type: "rect",
        xref: "paper",
        yref: "y",
        x0: 0,
        x1: 1,
        y0: -0.5 + i,
        y1: 0.5 + i,
        fillcolor: i % 2 === 0 ? "rgba(0, 0, 0, 0.03)" : "rgba(0, 0, 0, 0)",
        line: { width: 0 },
      })),
      margin: { t: 30, l: featureNameMaxLength * 16 * 0.6 + 10, b: 50, r: 0 },
      showlegend: false,
      xaxis: {
        showgrid: false,
        zeroline: true,
        title: {
          text: "Score decrease",
          font: {
            family: "GeistMono, monospace",
            size: 14,
            color: "#2f3037",
          },
        },
      },
      yaxis: {
        showgrid: true,
        tickfont: {
          family: "GeistMono, monospace",
          size: 16,
          color: "#2f3037",
        },
      },
    },
    frames: [],
  };
});
const barplot = computed(() => {
  const df = dataframes.value[selected.value];
  const trace = {
    x: df.data.flat(),
    y: df.index,
    type: "bar",
    orientation: "h",
  };
  const featureNameMaxLength = Math.max(...df.index.map((i) => i.toString().length));
  return {
    data: [trace],
    layout: {
      shapes: trace.x.map((_, i) => ({
        type: "rect",
        xref: "paper",
        yref: "y",
        x0: 0,
        x1: 1,
        y0: -0.5 + i,
        y1: 0.5 + i,
        fillcolor: i % 2 === 0 ? "rgba(0, 0, 0, 0.03)" : "rgba(0, 0, 0, 0)",
        line: { width: 0 },
      })),
      margin: { t: 30, l: featureNameMaxLength * 16 * 0.6 + 10, b: 50, r: 35 },
      xaxis: {
        title: {
          text: "Model coefficient value",
          font: {
            family: "Geist, sans-serif",
            size: 14,
            color: "#2f3037",
          },
        },
      },
      yaxis: {
        tickfont: {
          family: "GeistMono, monospace",
          size: 16,
          color: "#2f3037",
        },
      },
    },
    frames: [],
  };
});
const plotMethod = computed(() => {
  return dataframes.value[selected.value]?.method;
});
const plotSpec = computed(() => {
  return plotMethod.value === "permutation" ? boxplot.value : barplot.value;
});
const presentationContent = computed(() => ({
  media_type: MediaType.Plotly,
  value: plotSpec.value,
}));

async function copySelectedPlot() {
  if (selectedPlot.value) {
    await clipboard.copyElementAsPng(selectedPlot.value);
  }
}

function getMethodVerboseName(method: string, removePrefix: boolean) {
  const item = report.related_items.find((ri) => ri.key === method);
  if (item) {
    if (removePrefix) {
      return item.verbose_name.replace("Feature importance - ", "");
    }
    return item.verbose_name;
  }
  return method;
}
</script>

<template>
  <div class="features">
    <div class="heading">
      <div class="help">
        Discover which features drive your model’s predictions and how each influences results.
        Toggle between methods like permutation feature importance, mean decrease in impurity
        (available only for tree-based models), and coefficients (available only for linear models).
        Pinpoint the most influential features—and which ones might be holding your model back.
      </div>
      <DataSourceToggle v-model:is-test-datasource="isTestDatasource" />
    </div>
    <div class="plots">
      <div class="block-title label-s">
        <div><i class="title-icon icon icon-bar-chart-2" /> Feature importance</div>
        <div class="actions">
          <DropdownButton
            :label="getMethodVerboseName(dataframes[selected].method, true)"
            help-label="Method:&nbsp"
            icon-right="icon-chevron-down"
            kind="tertiary"
          >
            <DropdownButtonItem
              v-for="(df, index) in dataframes"
              :key="df.method"
              :label="getMethodVerboseName(df.method, true)"
              @click="selected = index"
              :data-method="df.method"
            />
          </DropdownButton>
          <AddToPresentationMenu
            :name="getMethodVerboseName(dataframes[selected].method, false)"
            :report_id="report.id"
            :media_type="presentationContent.media_type"
            :value="presentationContent.value"
          />
          <SimpleButton icon-left="icon-copy" kind="tertiary" @click="copySelectedPlot" />
        </div>
      </div>
      <div :class="['plot', plotMethod]" ref="selectedPlot">
        <PlotlyWidget :spec="plotSpec" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.features {
  display: flex;
  height: 100%;
  flex-direction: column;
  margin: var(--spacing-24) 0;

  .heading {
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    .help {
      flex: 2 1 0;
      margin-bottom: var(--spacing-12);
    }
  }

  .plots {
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    .plot {
      max-width: min(800px, 80dvw);
      max-height: 600px;
      flex-grow: 1;
    }
  }
}
</style>
